import { Box, Grid, Typography, IconButton, Button } from '@material-ui/core';
import React, { memo, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import {
  Loop as LoopIcon,
  CalendarToday as CalendarIcon,
  ChevronRight as ChevronRightIcon,
  VisibilityOutlined,
} from '@material-ui/icons';
import { unpackToTime } from '@campfire/hot-date';
import { SectionCollapsible } from '../../common/SectionCollapsible';
import { SectionHeader } from '../../common/SectionHeader';
import { SectionInstruction } from '../../common/SectionInstruction';
import { useVolunteerCommonProfileContext } from '../../VolunteerCommonProfileContext';
import { VolunteerCommonProfileActivityEnrolment } from '../../__generated__/VolunteerCommonProfileActivityEnrolment';
import { EnrolmentForecast } from './EnrolmentForecast';
import { ActivityEnrolmentDialog } from './ActivityEnrolmentDialog';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';

export const VolunteerActivityEnrolmentsSection = memo(
  ({
    activityEnrolments,
    volunteerId,
    profileId,
  }: {
    activityEnrolments: VolunteerCommonProfileActivityEnrolment[];
    volunteerId: string;
    profileId: string;
  }) => {
    const { theme } = useCampfireTheme();
    const {
      volunteerNameOptions: { preferredName },
      isHistoricalActivityEnrolment,
      setIsHistoricalActivityEnrolment,
    } = useVolunteerCommonProfileContext();

    return (
      <SectionCollapsible
        sectionHeader={<SectionHeader title='Activity Enrolments' />}
        sectionActions={
          <Button
            variant='outlined'
            startIcon={<VisibilityOutlined />}
            style={{
              height: 36,
              borderRadius: 6,
              border: '1px solid #DCDCDC',
              color: theme?.color?.grey?.neutral400,
              textTransform: 'capitalize',
              marginTop: '4px',
            }}
            onClick={() => setIsHistoricalActivityEnrolment(!isHistoricalActivityEnrolment)}
          >
            <Typography
              style={{
                fontWeight: 600,
                fontSize: '13px',
                lineHeight: '19px',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {`Viewing: ${isHistoricalActivityEnrolment ? 'Historical' : 'Current'}`}
            </Typography>
          </Button>
        }
        instruction={<SectionInstruction>{`All activities that ${preferredName} is enrolled in`}</SectionInstruction>}
        collapseContent={
          <VolunteerActivityEnrolmentsBlock
            activityEnrolments={activityEnrolments}
            volunteerId={volunteerId}
            profileId={profileId}
          />
        }
        expandText='View Activity Enrolments'
        idStub='activity-enrolments'
      />
    );
  }
);

export const VolunteerActivityEnrolmentsBlock = ({
  activityEnrolments,
  volunteerId,
  profileId,
}: {
  activityEnrolments: VolunteerCommonProfileActivityEnrolment[];
  volunteerId: string;
  profileId: string;
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedActivityEnrolment, setSelectedActivityEnrolment] = useState<VolunteerCommonProfileActivityEnrolment>();
  const {
    volunteerNameOptions: { preferredName },
    isHistoricalActivityEnrolment,
  } = useVolunteerCommonProfileContext();

  const { theme } = useCampfireTheme();

  const onSelectEnrolment = (activityEnrolment: VolunteerCommonProfileActivityEnrolment) => {
    setSelectedActivityEnrolment(activityEnrolment);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedActivityEnrolment(undefined);
  };

  const parsedActivityEnrolments = useMemo(() => {
    if (isHistoricalActivityEnrolment) {
      return activityEnrolments
        .filter(
          (ae, index) =>
            activityEnrolments.findIndex((fi) => ae.activity.activityId === fi.activity.activityId) === index
        )
        .filter(
          (activityEnrolment) =>
            activityEnrolment.dateRemoved &&
            activityEnrolments.filter(
              (item) => item.activity.activityId === activityEnrolment.activity.activityId && !item.dateRemoved
            ).length === 0
        );
    }
    return activityEnrolments.filter(
      (activityEnrolment) => activityEnrolment.activityEnrolmentId !== null && activityEnrolment.dateRemoved === null
    );
  }, [activityEnrolments, isHistoricalActivityEnrolment]);

  return (
    <>
      <Typography variant='caption'>{`Activities that ${preferredName} is enrolled in`}</Typography>
      <Box>
        {parsedActivityEnrolments.map((activityEnrolment) => {
          const recurrenceHumanReadable =
            activityEnrolment.activity.__typename === 'VOLUNTEER_RecurringActivityType'
              ? activityEnrolment.activity.schedule.recurrences[0].humanReadable
              : 'Flexible';

          const activityStartTime = unpackToTime(activityEnrolment.activity.startTime).toFormat('h:mm a');
          const activityEndTime = unpackToTime(activityEnrolment.activity.endTime).toFormat('h:mm a');

          const getTime = () => {
            const { startDate, endDate } = activityEnrolment.activity;
            if (!startDate && !endDate) {
              return '';
            }

            const diffNow = DateTime.fromISO(startDate)
              .diffNow()
              .as('days');
            if (diffNow > 0) {
              return `Starts ${DateTime.fromISO(startDate).toFormat('dd')} ${DateTime.fromISO(startDate).toFormat(
                'LLLL, y'
              )}`;
            }
            if (diffNow <= 0 && endDate) {
              return `Ends ${DateTime.fromISO(endDate).toFormat('dd')} ${DateTime.fromISO(endDate).toFormat(
                'LLLL, y'
              )}`;
            }
            return '';
          };
          const time = getTime();

          return (
            <Box
              key={activityEnrolment.activityEnrolmentId}
              display='flex'
              style={{
                backgroundColor: '#F9F9F9',
                boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.25)',
                borderRadius: '8px',
                marginTop: '16px',
                cursor: 'pointer',
              }}
              onClick={() => onSelectEnrolment(activityEnrolment)}
            >
              <Grid container>
                <Grid item xs={4}>
                  <Box py='12px' pl='16px' pr='10px'>
                    <Typography style={{ fontSize: '16px', fontWeight: 700 }}>
                      {activityEnrolment.activity.name}
                    </Typography>
                    <Typography style={{ fontSize: '12px', color: theme.color.grey.neutral200 }}>
                      {activityEnrolment.activity.program.name}
                    </Typography>
                    <Box display='flex' alignItems='center' marginTop='16px'>
                      {recurrenceHumanReadable && (
                        <LoopIcon
                          style={{
                            width: '15px',
                            height: '15px',
                            paddingRight: '4px',
                            marginLeft: '-2px',
                            color: theme.color.grey.neutral200,
                          }}
                        />
                      )}
                      <Typography
                        style={{
                          fontSize: '11px',
                          color: theme.color.grey.neutral200,
                        }}
                      >
                        {recurrenceHumanReadable}
                        {activityEnrolment.activity.startTime && activityEnrolment.activity.endTime
                          ? `, ${activityStartTime} - ${activityEndTime}`
                          : ''}
                      </Typography>
                    </Box>

                    {time && (
                      <Box display='flex' alignItems='center'>
                        <CalendarIcon
                          style={{ color: theme.color.grey.neutral200, fontSize: 12, marginRight: '6px' }}
                        />
                        <Typography style={{ fontSize: 11, color: theme.color.grey.neutral200, marginTop: '1px' }}>
                          {time}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={8}>
                  <Box display='flex' height='100%'>
                    <EnrolmentForecast volunteerId={volunteerId} activityEnrolment={activityEnrolment} />
                    <Box display='flex' width='56px' alignItems='center' justifyContent='center'>
                      <IconButton>
                        <ChevronRightIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Box>
      {selectedActivityEnrolment && (
        <ActivityEnrolmentDialog
          open={openDialog}
          handleClose={handleCloseDialog}
          activityEnrolment={selectedActivityEnrolment}
          volunteerId={volunteerId}
          profileId={profileId}
        />
      )}
    </>
  );
};
